// External
import { useMemo } from "react";
import _ from "lodash";

// Internal
import {
  SECTION_ID_TO_CONFIG_KEY,
  ITEM_ID_TO_CONFIG_ID,
  GRADING_PERIOD_COLUMNS_SECTION_ID,
  GRADE_SCALE_VIEW_TYPES,
} from "../../../modules/constants";
import {
  mapDisplayOrderToItems,
  getAcrossGradingPeriodsItems,
  getGradingPeriodWiseItems,
} from "../../../modules/utils";

// Relative
import { createColumnItem } from "../modules/viewModelHelpers";

export const useColumnViewModel = ({
  configuredSections,
  transcriptConfiguration,
  selectedGradeMap,
  gradingPeriods,
  selectedGradeScaleView,
  gradeScaleDescriptionMap,
  manageColumnItemIdSet,
  defaultGradeScalesConfig,
  orgGradeScalesData,
  curriculumProgramsData,
  language,
  t,
}) => {
  const sectionViewModel = useMemo(() => {
    return _.map(configuredSections, section => {
      const sectionId = section.id;
      const configKey = SECTION_ID_TO_CONFIG_KEY[sectionId];
      const sectionConfig = _.get(transcriptConfiguration, configKey, {});
      const displayOrder = _.get(sectionConfig, "displayOrder", []);
      const sectionConfigMap = _.get(sectionConfig, "config", {});

      const sectionItemsMeta = _.map(section.items || [], item => {
        const itemConfigId =
          ITEM_ID_TO_CONFIG_ID[item.id] || _.toUpper(item.id);
        return {
          ...item,
          itemConfigId,
        };
      });

      const metaLookup = _.keyBy(sectionItemsMeta, "itemConfigId");

      const buildItem = ({ itemId, itemConfig, itemMeta, idPrefix = "" }) => {
        const gradeScaleDescription =
          sectionId === GRADING_PERIOD_COLUMNS_SECTION_ID
            ? _.get(gradeScaleDescriptionMap, itemId, "")
            : "";

        return createColumnItem({
          itemId,
          itemConfig,
          itemMeta,
          idPrefix,
          language,
          t,
          gradeScaleDescription,
          isManageColumnItem: manageColumnItemIdSet.has(itemId),
        });
      };

      const shouldUseAcrossView =
        sectionId === GRADING_PERIOD_COLUMNS_SECTION_ID &&
        selectedGradeMap &&
        selectedGradeScaleView ===
          GRADE_SCALE_VIEW_TYPES.ACROSS_GRADING_PERIODS;

      let items = [];
      if (shouldUseAcrossView) {
        items = getAcrossGradingPeriodsItems({
          sectionConfig,
          metaLookup,
          buildItem,
        });
      } else if (_.isEmpty(displayOrder)) {
        items = getGradingPeriodWiseItems({
          selectedGradeMap,
          gradingPeriods,
          sectionConfig,
          defaultGradeScalesConfig:
            sectionId === GRADING_PERIOD_COLUMNS_SECTION_ID
              ? defaultGradeScalesConfig
              : undefined,
          orgGradeScalesData,
          curriculumProgramsData,
          metaLookup,
          buildItem,
        });
      } else {
        items = mapDisplayOrderToItems({
          itemDisplayOrder: displayOrder,
          itemConfigMap: sectionConfigMap,
          metaLookup,
          buildItem,
        });
      }

      const enableReorder =
        sectionId === "grading_period_aggregates_for_row" ||
        Boolean(section.isDynamic) ||
        _.some(sectionItemsMeta, "isReorderable");

      return {
        sectionId,
        title:
          sectionId === GRADING_PERIOD_COLUMNS_SECTION_ID && shouldUseAcrossView
            ? t("transcript:grading_period_columns_across_all")
            : t(`transcript:${sectionId}`),
        items,
        enableReorder,
        showSettings: sectionId === "grading_period_columns",
        showManageColumns: sectionId === "grading_period_columns",
      };
    });
  }, [
    configuredSections,
    curriculumProgramsData,
    defaultGradeScalesConfig,
    gradeScaleDescriptionMap,
    gradingPeriods,
    language,
    manageColumnItemIdSet,
    orgGradeScalesData,
    selectedGradeMap,
    selectedGradeScaleView,
    t,
    transcriptConfiguration,
  ]);

  return { sectionViewModel };
};
